import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BobineLogo from '../components/BobineLogo';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;

const AuthPage = ({ onLogin }) => {
  const { t } = useLanguage();
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot' | 'reset'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/dashboard';
  const refCode = searchParams.get('ref') || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const body = mode === 'login' ? { email, password } : { name, email, password };
      const res = await axios.post(`${API}${endpoint}`, body);
      if (res.data.status === 'success') {
        localStorage.setItem('bobine_token', res.data.token);
        localStorage.setItem('bobine_user', JSON.stringify(res.data.user));
        onLogin(res.data.user);

        // Auto-apply referral code if present in URL
        if (refCode && mode === 'register') {
          try {
            await axios.post(`${API}/referral/apply-code`, { referral_code: refCode }, {
              headers: { Authorization: `Bearer ${res.data.token}` }
            });
          } catch { /* silent — code may be invalid */ }
        }

        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess('Un code de réinitialisation a été envoyé à votre adresse email.');
      setMode('reset');
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email,
        code: resetCode,
        new_password: newPassword,
      });
      setSuccess('Le fil de votre histoire est de nouveau entre vos mains.');
      // Auto-login and redirect to dashboard after 3 seconds
      setTimeout(async () => {
        try {
          const loginRes = await axios.post(`${API}/auth/login`, { email, password: newPassword });
          if (loginRes.data.status === 'success') {
            localStorage.setItem('bobine_token', loginRes.data.token);
            localStorage.setItem('bobine_user', JSON.stringify(loginRes.data.user));
            onLogin(loginRes.data.user);
            navigate('/dashboard', { replace: true });
          }
        } catch {
          setMode('login');
          setSuccess('');
        }
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Code invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '14px 18px', borderRadius: 8,
    fontFamily: "'Georgia', serif", fontSize: 15,
    backgroundColor: 'rgba(212,175,55,0.04)',
    border: '1px solid rgba(212,175,55,0.2)',
    color: '#FAF3E0', outline: 'none',
    transition: 'border-color 0.3s',
  };

  const focusStyle = (e) => { e.target.style.borderColor = '#D4AF37'; };
  const blurStyle = (e) => { e.target.style.borderColor = '#D4AF37'; };

  const titles = {
    login: t('auth_title'),
    register: t('auth_register_title'),
    forgot: t('auth_forgot'),
    reset: t('auth_reset_title'),
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#000' }}>
      {/* Header identique aux autres pages */}
      <header style={{ borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <BobineLogo size={22} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <LanguageSelector />
            <button onClick={() => navigate('/')}
              style={{ fontFamily: "'Georgia', serif", fontSize: 12, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', color: '#D4AF37', backgroundColor: 'transparent', border: '1px solid rgba(212,175,55,0.2)' }}>
              {t('dash_back')}
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        <style>{`
          @keyframes bobineGlow {
            0%, 100% { filter: drop-shadow(0 0 8px rgba(212,175,55,0.3)); }
            50% { filter: drop-shadow(0 0 20px rgba(212,175,55,0.7)) drop-shadow(0 0 40px rgba(212,175,55,0.2)); }
          }
        `}</style>
        {/* Bouton Retour à gauche, dans un container large */}
        <div style={{ maxWidth: 1000, margin: '0 auto 24px' }}>
          <button onClick={() => navigate('/')} data-testid="back-btn"
            style={{ fontFamily: "'Georgia', serif", fontSize: 14, padding: '10px 20px', borderRadius: 8, cursor: 'pointer', color: '#D4AF37', backgroundColor: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.3)', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500, transition: 'all 0.3s' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(212,175,55,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(212,175,55,0.08)'; }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            {t('dash_back')}
          </button>
        </div>

        <div className="max-w-md mx-auto">
        {/* Logo décoratif */}
        <div className="text-center" style={{ marginBottom: 32 }}>
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: 16 }}>
            <img
              src="/bobine-face-logo.png"
              alt="Bobine"
              data-testid="auth-bobine-logo"
              style={{
                width: 72, height: 72,
                objectFit: 'contain',
                animation: 'spin 30s linear infinite, bobineGlow 3s ease-in-out infinite',
              }}
            />
          </div>
          <BobineLogo size={32} />
          <p style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: '#D4AF37', fontStyle: 'italic', marginTop: 6 }}>
            Votre coffre-fort de souvenirs
          </p>
        </div>

        {/* Card */}
        <div style={{
          border: '1px solid rgba(212,175,55,0.2)',
          borderRadius: 14, padding: '36px 32px',
          backgroundColor: 'rgba(212,175,55,0.02)',
        }}>
          <h2 data-testid="auth-title" style={{
            fontFamily: "'Georgia', serif", fontSize: 22,
            color: '#D4AF37', textAlign: 'center', marginBottom: 12,
          }}>
            {titles[mode]}
          </h2>

          {mode === 'register' && (
            <p data-testid="auth-reassurance" style={{
              fontFamily: "'Georgia', serif", fontSize: 13,
              color: '#FAF3E0', textAlign: 'center',
              lineHeight: 1.6, marginBottom: 28, fontStyle: 'italic',
            }}>
              Créez votre accès au Coffre-fort pour sauvegarder vos Trésors et finaliser votre livre. Vos données sont cryptées, votre héritage est scellé.
            </p>
          )}

          {mode === 'forgot' && (
            <p style={{
              fontFamily: "'Georgia', serif", fontSize: 13,
              color: '#FAF3E0', textAlign: 'center',
              lineHeight: 1.6, marginBottom: 28, fontStyle: 'italic',
            }}>
              Entrez votre adresse email. Nous vous enverrons un code pour réinitialiser votre mot de passe.
            </p>
          )}

          {mode === 'reset' && (
            <p style={{
              fontFamily: "'Georgia', serif", fontSize: 13,
              color: '#FAF3E0', textAlign: 'center',
              lineHeight: 1.6, marginBottom: 28, fontStyle: 'italic',
            }}>
              Saisissez le code reçu par email et choisissez un nouveau mot de passe.
            </p>
          )}

          {(mode === 'login' || mode === 'register') && <div style={{ marginBottom: 16 }} />}

          {/* Success message */}
          {success && (
            <p data-testid="auth-success" style={{
              fontFamily: "'Georgia', serif", fontSize: 13,
              color: '#27ae60', textAlign: 'center', marginBottom: 16,
              padding: '10px 14px', borderRadius: 8,
              backgroundColor: 'rgba(39,174,96,0.08)',
              border: '1px solid rgba(39,174,96,0.2)',
            }}>{success}</p>
          )}

          {/* Error message */}
          {error && (
            <p data-testid="auth-error" style={{
              fontFamily: "'Georgia', serif", fontSize: 13,
              color: '#c0392b', textAlign: 'center', marginBottom: 16,
            }}>{error}</p>
          )}

          {/* LOGIN / REGISTER form */}
          {(mode === 'login' || mode === 'register') && (
            <form onSubmit={handleSubmit}>
              {mode === 'register' && (
                <div style={{ marginBottom: 16 }}>
                  <input
                    type="text" placeholder={t('auth_name')}
                    value={name} onChange={e => setName(e.target.value)}
                    required data-testid="auth-name-input"
                    style={inputStyle} onFocus={focusStyle} onBlur={blurStyle}
                  />
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <input
                  type="email" placeholder={t('auth_email')}
                  value={email} onChange={e => setEmail(e.target.value)}
                  required data-testid="auth-email-input"
                  style={inputStyle} onFocus={focusStyle} onBlur={blurStyle}
                />
              </div>
              <div style={{ marginBottom: 8, position: 'relative' }}>
                <input
                  type={showPassword ? "text" : "password"} placeholder={t('auth_password')}
                  value={password} onChange={e => setPassword(e.target.value)}
                  required data-testid="auth-password-input"
                  style={inputStyle} onFocus={focusStyle} onBlur={blurStyle}
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                    color: '#D4AF37', transition: 'color 0.3s',
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    {showPassword ? (
                      <><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
                    ) : (
                      <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
                    )}
                  </svg>
                </button>
              </div>

              {/* Lien Mot de passe oublié — uniquement en mode login */}
              {mode === 'login' && (
                <div style={{ textAlign: 'right', marginBottom: 20 }}>
                  <button
                    type="button"
                    onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}
                    data-testid="forgot-password-link"
                    style={{
                      background: 'none', border: 'none',
                      fontFamily: "'Georgia', serif", fontSize: 12,
                      color: '#D4AF37', cursor: 'pointer',
                      transition: 'color 0.3s',
                    }}
                    onMouseEnter={e => { e.target.style.color = '#D4AF37'; }}
                    onMouseLeave={e => { e.target.style.color = '#D4AF37'; }}
                  >
                    {t('auth_forgot')}
                  </button>
                </div>
              )}

              {mode === 'register' && <div style={{ marginBottom: 16 }} />}

              <button
                type="submit" disabled={loading}
                data-testid="auth-submit-button"
                style={{
                  width: '100%', padding: '14px 0', borderRadius: 8,
                  fontFamily: "'Georgia', serif", fontSize: 16, fontWeight: 600,
                  background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000',
                  border: 'none', cursor: loading ? 'wait' : 'pointer',
                  letterSpacing: 0.5, transition: 'all 0.3s',
                  boxShadow: '0 4px 20px rgba(212,175,55,0.3)',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {loading ? '...' : mode === 'login' ? t('auth_login') : t('auth_register')}
              </button>
            </form>
          )}

          {/* FORGOT PASSWORD form */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgotSubmit}>
              <div style={{ marginBottom: 24 }}>
                <input
                  type="email" placeholder={t('auth_email')}
                  value={email} onChange={e => setEmail(e.target.value)}
                  required data-testid="forgot-email-input"
                  style={inputStyle} onFocus={focusStyle} onBlur={blurStyle}
                />
              </div>
              <button
                type="submit" disabled={loading}
                data-testid="forgot-submit-button"
                style={{
                  width: '100%', padding: '14px 0', borderRadius: 8,
                  fontFamily: "'Georgia', serif", fontSize: 16, fontWeight: 600,
                  background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000',
                  border: 'none', cursor: loading ? 'wait' : 'pointer',
                  letterSpacing: 0.5, transition: 'all 0.3s',
                  boxShadow: '0 4px 20px rgba(212,175,55,0.3)',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {loading ? '...' : t('auth_send_code')}
              </button>
            </form>
          )}

          {/* RESET PASSWORD form */}
          {mode === 'reset' && (
            <form onSubmit={handleResetSubmit} autoComplete="off">
              {/* Hidden email field for the browser */}
              <input type="hidden" name="email" value={email} />
              <div style={{ marginBottom: 16 }}>
                <input
                  type="text" placeholder="Code à 6 caractères"
                  value={resetCode} onChange={e => setResetCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                  required data-testid="reset-code-input"
                  autoComplete="one-time-code"
                  inputMode="text"
                  name="otp-code"
                  style={{ ...inputStyle, textAlign: 'center', letterSpacing: 8, fontSize: 22, fontWeight: 600 }}
                  onFocus={focusStyle} onBlur={blurStyle}
                  maxLength={6}
                />
              </div>
              <div style={{ marginBottom: 24, position: 'relative' }}>
                <input
                  type={showNewPassword ? "text" : "password"} placeholder={t('auth_new_password')}
                  value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  required data-testid="reset-password-input"
                  autoComplete="new-password"
                  style={inputStyle} onFocus={focusStyle} onBlur={blurStyle}
                  minLength={6}
                />
                <button
                  type="button"
                  data-testid="toggle-new-password-visibility"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                    color: '#D4AF37', transition: 'color 0.3s',
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    {showNewPassword ? (
                      <><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
                    ) : (
                      <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
                    )}
                  </svg>
                </button>
              </div>
              <button
                type="submit" disabled={loading}
                data-testid="reset-submit-button"
                style={{
                  width: '100%', padding: '14px 0', borderRadius: 8,
                  fontFamily: "'Georgia', serif", fontSize: 16, fontWeight: 600,
                  background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000',
                  border: 'none', cursor: loading ? 'wait' : 'pointer',
                  letterSpacing: 0.5, transition: 'all 0.3s',
                  boxShadow: '0 4px 20px rgba(212,175,55,0.3)',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {loading ? '...' : t('auth_reset_submit')}
              </button>
            </form>
          )}

          {/* Toggle links */}
          <div className="text-center" style={{ marginTop: 20 }}>
            {(mode === 'login' || mode === 'register') && (
              <button
                onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); setSuccess(''); }}
                data-testid="auth-toggle"
                style={{
                  background: 'none', border: 'none',
                  fontFamily: "'Georgia', serif", fontSize: 13,
                  color: '#D4AF37', cursor: 'pointer',
                }}
              >
                {mode === 'login' ? `${t('auth_no_account')} ${t('auth_create')}` : `${t('auth_has_account')} ${t('auth_connect')}`}
              </button>
            )}
            {(mode === 'forgot' || mode === 'reset') && (
              <button
                onClick={() => { setMode('login'); setError(''); setSuccess(''); setResetCode(''); setNewPassword(''); }}
                data-testid="back-to-login"
                style={{
                  background: 'none', border: 'none',
                  fontFamily: "'Georgia', serif", fontSize: 13,
                  color: '#D4AF37', cursor: 'pointer',
                }}
              >
                {t('auth_back_login')}
              </button>
            )}
          </div>
        </div>

        <div className="text-center" style={{ marginTop: 24 }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none', border: 'none',
              fontFamily: "'Georgia', serif", fontSize: 13,
              color: '#D4AF37', cursor: 'pointer',
            }}
          >
            {t('nav_home')}
          </button>
        </div>
        </div>
      </main>
    </div>
  );
};

export default AuthPage;
